/**
 * Input Masking Utilities
 * Provides auto-formatting for phone numbers, PAN cards, pincodes, and currency
 */

// ====================
// Phone Number Masking
// ====================

/**
 * Format phone number as user types: 9876 543 210
 */
export function formatPhoneNumber(value: string): string {
  // Remove all non-digits
  const digits = value.replace(/\D/g, '');
  
  // Limit to 10 digits
  const limited = digits.slice(0, 10);
  
  // Format as: XXXX XXX XXX
  if (limited.length <= 4) {
    return limited;
  } else if (limited.length <= 7) {
    return `${limited.slice(0, 4)} ${limited.slice(4)}`;
  } else {
    return `${limited.slice(0, 4)} ${limited.slice(4, 7)} ${limited.slice(7)}`;
  }
}

/**
 * Strip formatting from phone number to get raw digits
 */
export function unformatPhoneNumber(value: string): string {
  return value.replace(/\D/g, "");
}

/**
 * Validate phone number format (+91XXXXXXXXXX)
 */
export function isValidPhone(value: string): boolean {
  return /^\+91[6-9]\d{9}$/.test(value);
}

// ====================
// PAN Card Masking
// ====================

/**
 * Format PAN card as user types: ABCDE1234F
 */
export function formatPANCard(value: string): string {
  // Convert to uppercase and remove spaces
  const cleaned = value.toUpperCase().replace(/\s/g, '');
  
  // Limit to 10 characters
  const limited = cleaned.slice(0, 10);
  
  // Allow only letters for first 5 and last 1, digits for middle 4
  let formatted = '';
  for (let i = 0; i < limited.length; i++) {
    const char = limited[i];
    if (i < 5 || i === 9) {
      // First 5 and last character should be letters
      if (/[A-Z]/.test(char)) {
        formatted += char;
      }
    } else {
      // Middle 4 should be digits
      if (/\d/.test(char)) {
        formatted += char;
      }
    }
  }
  
  return formatted;
}

/**
 * Validate PAN card format
 */
export function isValidPAN(value: string): boolean {
  return /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(value);
}

// ====================
// Pincode Masking
// ====================

/**
 * Format pincode as user types (6 digits only)
 */
export function formatPincode(value: string): string {
  // Remove all non-digits
  const digits = value.replace(/\D/g, '');
  
  // Limit to 6 digits
  return digits.slice(0, 6);
}

/**
 * Validate pincode format
 */
export function isValidPincode(value: string): boolean {
  return /^[1-9][0-9]{5}$/.test(value);
}

// ====================
// Currency Masking
// ====================

/**
 * Format currency in Indian format: 1,23,45,678
 */
export function formatIndianCurrency(value: number | string): string {
  const num = typeof value === 'string' ? parseFloat(value.replace(/,/g, '')) : value;
  
  if (isNaN(num)) return '';
  
  return num.toLocaleString('en-IN', {
    maximumFractionDigits: 2,
  });
}

/**
 * Format currency input as user types
 */
export function formatCurrencyInput(value: string): string {
  // Remove all non-digits and non-decimal points
  const cleaned = value.replace(/[^\d.]/g, '');
  
  // Allow only one decimal point
  const parts = cleaned.split('.');
  if (parts.length > 2) {
    return parts[0] + '.' + parts.slice(1).join('');
  }
  
  // Format the integer part
  const integerPart = parts[0] ? parseInt(parts[0]).toLocaleString('en-IN') : '';
  const decimalPart = parts[1] !== undefined ? '.' + parts[1].slice(0, 2) : '';
  
  return integerPart + decimalPart;
}

/**
 * Parse Indian formatted currency to number
 */
export function parseIndianCurrency(value: string): number {
  const cleaned = value.replace(/,/g, '');
  const num = parseFloat(cleaned);
  return isNaN(num) ? 0 : num;
}

// ====================
// CIBIL Score Masking
// ====================

/**
 * Format CIBIL score (3 digits, 300-900)
 */
export function formatCIBILScore(value: string): string {
  // Remove all non-digits
  const digits = value.replace(/\D/g, '');
  
  // Limit to 3 digits
  const limited = digits.slice(0, 3);
  
  // Validate range
  const num = parseInt(limited);
  if (limited.length === 3 && (num < 300 || num > 900)) {
    // Keep only first 2 digits if invalid range
    return limited.slice(0, 2);
  }
  
  return limited;
}

/**
 * Validate CIBIL score
 */
export function isValidCIBIL(value: number): boolean {
  return value >= 300 && value <= 900;
}

// ====================
// Percentage Masking
// ====================

/**
 * Format percentage (0-100)
 */
export function formatPercentage(value: string): string {
  // Remove all non-digits and non-decimal points
  const cleaned = value.replace(/[^\d.]/g, '');
  
  // Allow only one decimal point
  const parts = cleaned.split('.');
  if (parts.length > 2) {
    return parts[0] + '.' + parts.slice(1).join('');
  }
  
  // Limit integer part to 3 digits (for 100)
  const integerPart = parts[0] ? parts[0].slice(0, 3) : '';
  const decimalPart = parts[1] !== undefined ? '.' + parts[1].slice(0, 2) : '';
  
  const result = integerPart + decimalPart;
  const num = parseFloat(result);
  
  // Cap at 100
  if (num > 100) {
    return '100';
  }
  
  return result;
}

/**
 * Validate percentage
 */
export function isValidPercentage(value: number): boolean {
  return value >= 0 && value <= 100;
}

// ====================
// Interest Rate Masking
// ====================

/**
 * Format interest rate (0-30%)
 */
export function formatInterestRate(value: string): string {
  // Remove all non-digits and non-decimal points
  const cleaned = value.replace(/[^\d.]/g, '');
  
  // Allow only one decimal point
  const parts = cleaned.split('.');
  if (parts.length > 2) {
    return parts[0] + '.' + parts.slice(1).join('');
  }
  
  // Limit integer part to 2 digits (for 30)
  const integerPart = parts[0] ? parts[0].slice(0, 2) : '';
  const decimalPart = parts[1] !== undefined ? '.' + parts[1].slice(0, 2) : '';
  
  const result = integerPart + decimalPart;
  const num = parseFloat(result);
  
  // Cap at 30
  if (num > 30) {
    return '30';
  }
  
  return result;
}

/**
 * Validate interest rate
 */
export function isValidInterestRate(value: number): boolean {
  return value >= 0 && value <= 30;
}

// ====================
// General Number Formatting
// ====================

/**
 * Format a number with commas for display
 */
export function formatNumberWithCommas(value: number | string): string {
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) return '';
  return num.toLocaleString('en-IN');
}

/**
 * Parse a formatted number string to number
 */
export function parseFormattedNumber(value: string): number {
  const cleaned = value.replace(/,/g, '');
  const num = parseFloat(cleaned);
  return isNaN(num) ? 0 : num;
}
